import { getStartOfDay, getEndOfDay, formatDate, formatToISO, addDaysToDate, parseDate } from '@travelata-libs/utils';
import { computed, type Ref } from 'vue';
import { MS_IN_DAY, RANGE_DELIMITER } from './constants';
import {
  type CalendarModelValue,
  type CalendarSystemModelValue,
  type DateTimestamp,
  type DateISO,
  type CalendarDateRange,
} from '../Calendar.types';

export function sortRange(systemModelValue: CalendarDateRange<DateTimestamp>) {
  const { start, end } = systemModelValue;

  if (start === null || end === null) return;

  const [first, second] = [start, end].sort((a, b) => a - b);

  return {
    start: getStartOfDay(new Date(first).getTime()),
    end: getEndOfDay(new Date(second).getTime()),
  } as CalendarDateRange<DateTimestamp>;
}

export function dateBetweenMinAndMaxDate(date: DateTimestamp, minDate: Ref<Date | null>, maxDate: Ref<Date | null>) {
  const formattedMinDate = computed(() => (minDate.value ? getStartOfDay(minDate.value.getTime()) : null));
  const formattedMaxDate = computed(() => (maxDate.value ? getEndOfDay(maxDate.value.getTime()) : null));

  return (
    (formattedMinDate.value === null || date >= formattedMinDate.value) &&
    (formattedMaxDate.value === null || date <= formattedMaxDate.value)
  );
}

export function convertModelValueToTextString(range: boolean, innerModel: CalendarSystemModelValue): string {
  if (!innerModel) return '';

  const formattedStart = formatDate(innerModel.start, `eeeeee, dd ${range ? 'MMM' : 'MMMM'} yyyy`);
  const formattedEnd = formatDate(innerModel.end, 'eeeeee, dd MMM yyyy');

  if (range) {
    const start = formattedStart.replaceAll('.', '');
    const end = formattedEnd.replaceAll('.', '');

    return `${start} ${RANGE_DELIMITER} ${end}`;
  }

  return formattedStart;
}

export function convertModelValueToISO(
  range: boolean,
  innerModel: CalendarSystemModelValue,
  additionalDay: number,
): DateISO | CalendarDateRange<DateISO> | null {
  if (!innerModel) return null;

  if (range) {
    return {
      start:
        additionalDay > 0
          ? formatToISO(addDaysToDate(innerModel.start, -additionalDay))
          : formatToISO(innerModel.start),
      end: additionalDay > 0 ? formatToISO(addDaysToDate(innerModel.end, additionalDay)) : formatToISO(innerModel.end),
    } as CalendarDateRange<DateISO>;
  }

  const { start } = innerModel;

  return formatToISO(start);
}

function convertModelValueToDate(model: CalendarModelValue): CalendarModelValue {
  if (typeof model === 'number' || typeof model === 'string') {
    return parseDate(model);
  }

  if (model && typeof model === 'object' && 'start' in model && 'end' in model) {
    return {
      start: parseDate(model.start),
      end: parseDate(model.end),
    } as CalendarDateRange<Date>;
  }

  return model;
}

export function convertModelValueToTimestamp(range: boolean, model: CalendarModelValue): CalendarSystemModelValue {
  const formattedModel = convertModelValueToDate(model);

  if (range) {
    const rangeModelValue = (formattedModel as CalendarDateRange<Date>) ?? null;
    if (!rangeModelValue || !rangeModelValue.start || !rangeModelValue.end) return null;

    return {
      start: getStartOfDay(rangeModelValue.start.getTime()),
      end: getEndOfDay(rangeModelValue.end.getTime()),
    } as CalendarSystemModelValue;
  }

  const modelValue = (formattedModel as Date)?.getTime() ?? null;
  if (!modelValue) return null;

  const start = getStartOfDay(modelValue);
  return { start, end: start } as CalendarDateRange<DateTimestamp>;
}

export function convertTextToModelValue(value: string, range: boolean = false): CalendarModelValue {
  if (value.length === 0) return null;

  if (range) {
    const [startStr, endStr] = value.split(RANGE_DELIMITER);

    let start: Date | null = null;
    let end: Date | null = null;

    if (startStr) {
      start = parseDate(startStr.trim());
    }

    if (endStr) {
      end = parseDate(endStr.trim());
    }

    if (!start || !end) return null;

    return sortRange({
      start: getStartOfDay(start),
      end: getEndOfDay(end),
    }) as CalendarModelValue;
  }

  const date = parseDate(value.trim());

  if (!date) return null;

  return getStartOfDay(date) as CalendarModelValue;
}

export function getLastDayBySpan(day: DateTimestamp, firstDay: DateTimestamp, span?: number) {
  if (!span) {
    return day;
  }

  const daySpan = span - 1;

  if (Math.abs((firstDay - day) / MS_IN_DAY) > daySpan) {
    return firstDay > day ? firstDay - daySpan * MS_IN_DAY : firstDay + daySpan * MS_IN_DAY;
  }

  return day;
}
